import {ProjectSummary} from 'constants/SourceConstants';
import {User} from 'constants/UserConstants';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {
  MemberRequest,
  Organization,
  OrganizationInvite,
  OrganizationPermission,
  OrganizationPermissionRole,
  Team,
} from './OrganizationConstants';

export interface OrganizationUpdateData {
  disablePublic?: boolean;
  email: string;
  location?: string;
  name: string;
  website?: string;
  institution?: string;
}

export interface OrganizationCreateData {
  name: string;
  handle: string;
  lab?: number;
  institution?: string | null;
}

export interface MembersTabInfo {
  members: Array<User>;
  permissions: Array<OrganizationPermission>;
  userIdToMemberInfo: {[userId: string]: {lastSeen: string}};
  invites: Array<OrganizationInvite>;
  requests: Array<MemberRequest>;
}

export default {
  create(organization: OrganizationCreateData): JQueryPromise<Organization> {
    return net.postJSON({
      url: '/1/api/organizations/',
      data: organization,
    });
  },

  get(orgUrlIdentifier: string): JQueryPromise<Organization> {
    return net.getJSON(`/1/api/organizations/${orgUrlIdentifier}`);
  },

  update(
    organizationId: string,
    data: {
      disablePublic?: boolean;
      email: string;
      location?: string;
      name: string;
      website?: string;
      institution?: string;
    }
  ): Promise<Organization> {
    return net.postJSONPromise({
      url: `/1/api/organizations/${organizationId}`,
      data,
    });
  },

  delete(orgId: string): Promise<{}> {
    return net.deletePromise({url: `/1/api/organizations/${orgId}`});
  },

  tombstone(orgId: string): Promise<{}> {
    return TaskRunner.postJSONPromise({url: `/1/api/organizations/${orgId}:tombstone`});
  },

  fetchProjects(orgId: string): Promise<{projects: Array<ProjectSummary>}> {
    return net.getJSONPromise(`/1/api/organizations/${orgId}/projects`);
  },

  fetchMembers(
    orgId: string
  ): JQueryPromise<{members: Array<User>; permissions: Array<OrganizationPermission>}> {
    return net.getJSON(`/1/api/organizations/${orgId}/members`);
  },

  fetchTeams(orgId: string): JQueryPromise<{teams: Array<Team>}> {
    return net.getJSON(`/1/api/organizations/${orgId}/teams`);
  },

  fetchInfoForMembersTab(orgId: string): JQueryPromise<MembersTabInfo> {
    return net.getJSON(`/1/api/organizations/${orgId}/members`, {
      getMemberInfo: '1',
      getPendingMembers: '1',
    });
  },

  cancelInvite(orgId: string, email: string): JQueryPromise<{}> {
    return net.postJSON({
      url: `/1/api/organizations/${orgId}:cancel-invite`,
      data: {email},
    });
  },

  updateOrganizationPermission(
    orgId: string,
    userId: string,
    data: {memberLevel: OrganizationPermissionRole}
  ): JQueryPromise<{}> {
    return net.patchJSON({
      url: `/1/api/organizations/${orgId}/users/${userId}`,
      data,
    });
  },

  removeMember(orgId: string, userId: string): JQueryPromise<{}> {
    return net.delete_({
      url: `/1/api/organizations/${orgId}/users/${userId}`,
    });
  },

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setInitials(orgId: string, data: {initialsByUserId: {[userId: string]: string}; orgInitials?: string}) {
    return net.postJSON({
      url: `/1/api/organizations/${orgId}/actions/bulk-set-initials`,
      data,
    });
  },

  search({query}: {query: string}): JQueryPromise<{organizations: Array<Organization>}> {
    return net.getJSON('/1/api/organizations/', {q: query});
  },

  inviteUser(orgId: string, userIdentifier: string): JQueryPromise<{emails: number}> {
    return net.postJSON({
      url: `/1/api/organizations/${orgId}/users/`,
      data: {userIdentifier},
    });
  },

  addMember(orgId: string, userId: string): Promise<{}> {
    return net.postJSONPromise({
      url: `/1/api/organizations/${orgId}:add-member`,
      data: {userId},
    });
  },

  inviteUsers(orgId: string, userIds: Array<string>, emails: Array<string>): JQueryPromise<{emails: number}> {
    return net.postJSON({
      url: `/1/api/organizations/${orgId}/actions/invite-members`,
      data: {user_ids: userIds, emails},
    });
  },

  acceptInboundRequest(orgId: string, userId: string): JQueryPromise<{}> {
    return net.postJSON({
      url: `/1/api/organizations/${orgId}/organization-requests/${userId}/actions/accept`,
    });
  },
  rejectInboundRequest(orgId: string, userId: string): JQueryPromise<{}> {
    return net.postJSON({
      url: `/1/api/organizations/${orgId}/organization-requests/${userId}/actions/reject`,
    });
  },

  updateOrganizationAvatar(organizationId: string, avatarFile: File): Promise<Organization> {
    return net.postFileAsFormDataPromise({
      url: `/1/api/organizations/${organizationId}/actions/update-avatar`,
      key: 'avatar',
      file: avatarFile,
    });
  },

  removeOrganizationAvatar(organizationId: string): Promise<Organization> {
    return net.deletePromise({
      url: `/1/api/organizations/${organizationId}/actions/remove-avatar`,
    });
  },

  organizationURL(urlIdentifier: string, section?: string): string {
    const baseUrl = `/organizations/${urlIdentifier}`;
    if (section) {
      return `${baseUrl}/${section}`;
    }
    return baseUrl;
  },

  createTeamPageURL(urlIdentifier: string): string {
    return `/organizations/${urlIdentifier}/create-team`;
  },
};
